import type { FC } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PopupRequest } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import settings from 'src/settings';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import type { AfterLoginCallback } from 'src/components/app/context/LoginConsumer';
import LoginConsumer from 'src/components/app/context/LoginConsumer';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import AuthError from 'src/components/account/LoginPage/components/AuthError';

import { styles } from './index.styles';

interface AzureLoginComponentProps {
  onDone: () => void;
  afterLogin: AfterLoginCallback;
}

const AzureLoginComponent: FC<AzureLoginComponentProps> = ({
  onDone,
  afterLogin,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');
  const [isAuthError, setIsAuthError] = useState<boolean | undefined>(false);

  const { t } = useTranslation('login');
  const getApiToken = (id_token: string) =>
    HttpService.post(apiPaths.ACCOUNT.LOGIN_SSO, {
      id_token: id_token,
    })
      .then((response) => {
        afterLogin(response.data.key, onDone);
        return true;
      })
      .catch((error) => {
        setErrorMessage(error.response.data.non_field_errors[0]);
        return false;
      });

  const startSso = () => {
    setIsLoading(true);
    const msalInstance = new PublicClientApplication({
      auth: {
        clientId: settings.VITE_APP_ADAL_CLIENT_ID,
        redirectUri: settings.VITE_APP_ADAL_REDIRECT_URI,
      },
    });
    (async () => {
      await msalInstance.initialize();
      const popup_request: PopupRequest = {
        scopes: [],
      };
      await msalInstance
        .loginPopup(popup_request)
        .then(() => {
          const currentAccounts = msalInstance.getAllAccounts();
          if (currentAccounts.length === 0) {
            return;
          }
          const tokenRequest = {
            scopes: ['User.Read'],
            account: currentAccounts[0],
          };
          msalInstance
            .acquireTokenSilent(tokenRequest)
            .then((tokenResponse) => {
              getApiToken(tokenResponse.idToken);
            })
            .catch(() => {
              setIsAuthError(true);
              setErrorMessage(t('authErrorMessage'));
            });
        })
        .catch((error) =>
          setErrorMessage(error.errorMessage || t('errorMessage')),
        )
        .finally(() => setIsLoading(false));
    })();
  };

  return (
    <Box sx={styles.msalLogin}>
      <LoadingButton
        loading={isLoading}
        onClick={startSso}
        variant="cta"
        fullWidth
      >
        {t('oneClickLoginButton')}
      </LoadingButton>
      {errorMessage && (
        <ErrorMessage sx={styles.errorMsg}>
          {errorMessage}
          {isAuthError && <AuthError />}
        </ErrorMessage>
      )}
    </Box>
  );
};

const AzureLoginWrapper = ({ onDone }: { onDone: () => void }) => (
  <LoginConsumer
    render={(afterLogin: AfterLoginCallback) => (
      <AzureLoginComponent onDone={onDone} afterLogin={afterLogin} />
    )}
  />
);

export default AzureLoginWrapper;
